import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { parseReturnArcheId, returnToArche } from '../lib/returnArche';

/**
 * "Back to <the game>" at the top of a page a game opened in a new tab
 * (`?return=<arche id>` — see `lib/returnArche.ts`). Renders nothing without a
 * valid `?return=`.
 */
export function ReturnToArcheButton() {
  const archeId = parseReturnArcheId(useLocation().search);
  const navigate = useNavigate();
  // The words are the arche's own name, never the parameter. Non-critical: a
  // name that cannot be read leaves the button saying where it goes in general.
  const { data: metadata } = useQuery({
    queryKey: queryKeys.archeMetadata.detail(archeId ?? ''),
    queryFn: () => api.getArcheMetadata(archeId as string),
    enabled: archeId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  if (!archeId) return null;
  const name = metadata?.name?.trim();

  const onPress = () =>
    returnToArche(archeId, {
      historyLength: () => window.history.length,
      close: () => window.close(),
      isClosed: () => window.closed,
      // an in-place navigate: where a home-screen icon pins only the page a tab
      // LOADED (iPhone / iPad), the game's page loads itself on arrival (the
      // route guard, `archeNeedsPageLoad`)
      go: (path) => navigate(path),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    });

  return (
    <div className="mb-4">
      <Button size="lg" className="min-h-11" onClick={onPress}>
        <ArrowLeft className="h-5 w-5 mr-2" />
        {name ? `Back to ${name}` : 'Back to the game'}
      </Button>
    </div>
  );
}
